import { Button, Container, Grid, Paper, Stack, Typography, Box } from '@mui/material';
import { FormProvider, TextField } from '../../../../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { BASE_URL } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

export const RoomStatusReport = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        from_date: Yup.string().required('From date is required'),
        to_date: Yup.string().required('To date is required')
      })
    ),
    defaultValues: {
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0]
    },
    mode: 'all'
  });

  const { watch } = methods;

  function formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const handleViewPdfReport = () => {
    const params = {
      from_date: watch('from_date'),
      to_date: watch('to_date')
    };
    window.open(
      `/print${pathname}?refId=${JSON.stringify(params)}`,
      '_blank'
    );
  };

  const handleExcelReport = async () => {
    try {
      const params = {
        from_date: watch('from_date'),
        to_date: watch('to_date')
      };
      
      const response = await axios.get(
        `${BASE_URL}/print/hospital/nurse-module/room-status-report?refId=${JSON.stringify(params)}`
      );
      
      console.log("API response", response);
      
      if (response.data.success) {
        const filteredData = response?.data?.data || [];
        
        if (filteredData.length > 0) {
          const fileName = `Room_Status_Report_${formatDate(watch('from_date'))}_to_${formatDate(watch('to_date'))}`;
          
          const formattedData = filteredData.map((item, index) => ({
            "Sr. No.": index + 1,
            'Location': item?.location || '-',
            'Nurse Station': item?.nursingstation?.name || '-',
            'Dept. Unit': item?.departmentunit?.name || '-',
            'Floor': item?.floor?.name || '-',
            'Ward': item?.wards?.name || '-',
            'Room No.': item?.room_no || '-',
            'Bed No.': item?.beds?.map(bed => bed?.bed_no).join(", ") || '-',
            'Occupy Status': item?.beds?.map(bed => bed?.occupy_status ? 'Occupied' : 'Vacant').join(", ") || '-',
            'Patient Name': item?.patient ? 
              `${item.patient.name_suffix || ''} ${item.patient.first_name || ''} ${item.patient.last_name || ''}`.trim() : '-',
            'UHID': item?.patient?.uhid || '-',
            'Doctor': item?.doctorDetail?.name || '-'
          }));
          
          const ws = XLSX.utils.json_to_sheet(formattedData);
          
          // Adjust column widths
          const colWidths = [
            { wch: 8 },  // Sr. No.
            { wch: 15 }, // Location
            { wch: 20 }, // Nurse Station
            { wch: 15 }, // Dept. Unit
            { wch: 10 }, // Floor
            { wch: 15 }, // Ward
            { wch: 12 }, // Room No.
            { wch: 20 }, // Bed No.
            { wch: 15 }, // Occupy Status
            { wch: 25 }, // Patient Name
            { wch: 15 }, // UHID
            { wch: 20 }  // Doctor
          ];
          ws['!cols'] = colWidths;
          
          const wb = { Sheets: { 'Room Status': ws }, SheetNames: ['Room Status'] };
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(data, fileName + '.xlsx');
          
          toast.success('Excel report downloaded successfully');
        } else {
          toast.error('No Data Found For Selected Date Range');
        }
      } else {
        toast.error('Something Went Wrong');
      }
    } catch (error) {
      console.error('Error downloading Excel report:', error);
      toast.error('Failed to download Excel report');
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Room Status Report
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button 
                  id={`${lastPathSegment}Print`}
                  variant="contained" 
                  onClick={handleViewPdfReport}
                  disabled={!watch('from_date') || !watch('to_date')}
                >
                  View PDF Report
                </Button>
                <Button 
                  id={`${lastPathSegment}Excel`}
                  variant="contained" 
                  onClick={handleExcelReport}
                  disabled={!watch('from_date') || !watch('to_date')}
                >
                  Download Excel Report
                </Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            
            {/* Date Filter Box */}
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                    <TextField sx={{ flex: 0.8 }} name="from_date" type="date" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField sx={{ flex: 0.8 }} name="to_date" type="date" />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};

export default RoomStatusReport;